import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Save, 
  FileDown, 
  Trash2, 
  X, 
  History as HistoryIcon, 
  Pencil, 
  Copy, 
  User, 
  ChevronDown, 
  ChevronUp, 
  FileSpreadsheet,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Clock,
  Sparkles,
  Layers,
  Activity,
  Mic
} from 'lucide-react';
import Navbar from '../components/Navbar';
import AddFoodDialog from '../components/AddFoodDialog';
import AddParenteralDialog from '../components/AddParenteralDialog';
import VoiceRecallInput from '../components/VoiceRecallInput';
import BackToTop from '../components/BackToTop';
import StatusDot from '../components/StatusDot';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { generateCurrentCalculationPDF } from '../utils/pdfGenerator';
import { exportMicronutrientsToExcel } from '../utils/excelExporter';
import { calculateNutritionalStatus } from '../utils/nutritionalStatus';
import { recordsAPI, patientsAPI, adminAPI } from '../services/api';
import ConfirmDialog, { AlertDialog } from '../components/ConfirmDialog';
import { useConfirmDialog, useAlertDialog } from '../hooks/useDialog';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatNumber, formatKcal } from '../utils/numberFormat';
import { calculateRouteSplits, getItemRoute, isEnteralItem, getRouteClinicalBadge } from '../utils/routeClassification';
import { toast } from '../hooks/use-toast';

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const { dialog: confirmDialog, confirm, close: closeConfirm } = useConfirmDialog();
  const { dialog: alertDialog, alert: showAlert, close: closeAlert } = useAlertDialog();
  
  const [patient, setPatient] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [targetEnergy, setTargetEnergy] = useState(1600);
  const [oralItems, setOralItems] = useState([]);
  const [parenteralItems, setParenteralItems] = useState([]);
  const [activeInputTab, setActiveInputTab] = useState('oral'); // 'oral' or 'parenteral'
  const [analysisTab, setAnalysisTab] = useState('summary'); // 'summary' or 'micronutrients'
  
  const [showFoodDialog, setShowFoodDialog] = useState(false);
  const [showParenteralDialog, setShowParenteralDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingCopy, setLoadingCopy] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [editRoute, setEditRoute] = useState('oral');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [summaryLang, setSummaryLang] = useState('id'); // 'en' or 'id'

  // Load patient data
  useEffect(() => {
    const loadPatient = async () => {
      if (!patientId) {
        setLoadingPatient(false);
        return;
      }

      try {
        setLoadingPatient(true);
        const data = await patientsAPI.getById(patientId);
        setPatient(data);
      } catch (error) {
        console.error('Failed to load patient:', error);
      } finally {
        setLoadingPatient(false);
      }
    };

    loadPatient();
  }, [patientId]);

  // Calculate totals
  const calculateTotals = (items) => {
    return items.reduce(
      (acc, item) => ({
        energy: acc.energy + item.energy_kcal,
        protein: acc.protein + item.protein_g,
        carbs: acc.carbs + item.carbohydrate_g,
        fat: acc.fat + item.fat_g,
      }),
      { energy: 0, protein: 0, carbs: 0, fat: 0 }
    );
  };

  const oralTotals = calculateTotals(oralItems);
  const parenteralTotals = calculateTotals(parenteralItems);
  const grandTotal = {
    energy: oralTotals.energy + parenteralTotals.energy,
    protein: oralTotals.protein + parenteralTotals.protein,
    carbs: oralTotals.carbs + parenteralTotals.carbs,
    fat: oralTotals.fat + parenteralTotals.fat,
  };

  const routeSplits = calculateRouteSplits(oralItems, parenteralItems);
  const routeBadge = getRouteClinicalBadge(routeSplits.oralPct, routeSplits.enteralPct, routeSplits.parenteralPct);
  const displayedOralItems = oralItems.filter(item => getItemRoute(item) === 'oral');
  const displayedEnteralItems = oralItems.filter(item => getItemRoute(item) === 'enteral');

  const percentageOfTarget = targetEnergy > 0 ? formatNumber((grandTotal.energy / targetEnergy) * 100) : '0';
  const numericPct = targetEnergy > 0 ? (grandTotal.energy / targetEnergy) * 100 : 0;

  // Smart Recall Badge for clinical risk & overfeeding
  const getSmartRecallBadge = (pct) => {
    if (pct < 50) {
      return {
        label: 'Defisit Berat',
        badgeBg: 'bg-red-500/25 text-red-100 border-red-300/40',
        pillBg: 'bg-red-100 text-red-800 border-red-200',
        chartColor: 'text-red-600',
        dotColor: 'bg-red-300',
        isOverfeeding: false,
        isDeficit: true,
      };
    }
    if (pct >= 80 && pct <= 120) {
      return {
        label: 'Asupan Adekuat',
        badgeBg: 'bg-emerald-500/30 text-emerald-100 border-emerald-300/40',
        pillBg: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        chartColor: 'text-emerald-600',
        dotColor: 'bg-emerald-300',
        isOverfeeding: false,
        isDeficit: false,
      };
    }
    if (pct > 130) {
      return {
        label: 'Waspada Overfeeding',
        badgeBg: 'bg-rose-500/35 text-rose-100 border-rose-300/60 animate-pulse',
        pillBg: 'bg-rose-100 text-rose-800 border-rose-300 animate-pulse',
        chartColor: 'text-rose-600 font-extrabold',
        dotColor: 'bg-rose-300',
        isOverfeeding: true,
        isDeficit: false,
      };
    }
    if (pct >= 50 && pct < 80) {
      return {
        label: 'Perlu Peningkatan',
        badgeBg: 'bg-amber-500/25 text-amber-100 border-amber-300/40',
        pillBg: 'bg-amber-100 text-amber-800 border-amber-200',
        chartColor: 'text-amber-600',
        dotColor: 'bg-amber-300',
        isOverfeeding: false,
        isDeficit: false,
      };
    }
    // 120 - 130%
    return {
      label: 'Mendekati Batas Atas',
      badgeBg: 'bg-orange-500/25 text-orange-100 border-orange-300/40',
      pillBg: 'bg-orange-100 text-orange-800 border-orange-200',
      chartColor: 'text-orange-600',
      dotColor: 'bg-orange-300',
      isOverfeeding: false,
      isDeficit: false,
    };
  };

  const smartRecallBadge = getSmartRecallBadge(numericPct);

  // Oral percentages
  const calculateOralNutrientPercentages = (totals) => {
    const energy = totals.energy;
    if (energy === 0) {
      return { energyPercent: '0.0', proteinPercent: '0.0', carbPercent: '0.0', fatPercent: '0.0' };
    }
    return {
      energyPercent: targetEnergy > 0 ? formatNumber((energy / targetEnergy) * 100) : '0.0',
      proteinPercent: formatNumber((totals.protein * 4 / energy) * 100),
      carbPercent: formatNumber((totals.carbs * 4 / energy) * 100),
      fatPercent: formatNumber((totals.fat * 9 / energy) * 100)
    };
  };

  // Parenteral percentages
  const calculateParenteralNutrientPercentages = (items) => {
    if (items.length === 0) {
      return { energyPercent: '0.0', proteinPercent: '0.0', carbPercent: '0.0', fatPercent: '0.0' };
    }

    let totalProteinEnergy = 0;
    let totalCarbEnergy = 0;
    let totalFatEnergy = 0;

    items.forEach(item => {
      totalProteinEnergy += item.protein_g * 4;
      totalCarbEnergy += item.carbohydrate_g * (item.carb_factor || 4.0);
      totalFatEnergy += item.fat_g * (item.fat_factor || 9.0);
    });

    const totalEnergy = totalProteinEnergy + totalCarbEnergy + totalFatEnergy;
    if (totalEnergy === 0) {
      return { energyPercent: '0.0', proteinPercent: '0.0', carbPercent: '0.0', fatPercent: '0.0' };
    }

    return {
      energyPercent: targetEnergy > 0 ? formatNumber((totalEnergy / targetEnergy) * 100) : '0.0',
      proteinPercent: formatNumber((totalProteinEnergy / totalEnergy) * 100),
      carbPercent: formatNumber((totalCarbEnergy / totalEnergy) * 100),
      fatPercent: formatNumber((totalFatEnergy / totalEnergy) * 100)
    };
  };

  const oralPercentages = calculateOralNutrientPercentages(oralTotals);
  const parenteralPercentages = calculateParenteralNutrientPercentages(parenteralItems);

  // Grand total percentages
  const calculateGrandTotalPercentages = () => {
    const oralProteinEnergy = oralTotals.protein * 4;
    const oralCarbEnergy = oralTotals.carbs * 4;
    const oralFatEnergy = oralTotals.fat * 9;

    let parenteralProteinEnergy = 0;
    let parenteralCarbEnergy = 0;
    let parenteralFatEnergy = 0;

    parenteralItems.forEach(item => {
      parenteralProteinEnergy += item.protein_g * 4;
      parenteralCarbEnergy += item.carbohydrate_g * (item.carb_factor || 4.0);
      parenteralFatEnergy += item.fat_g * (item.fat_factor || 9.0);
    });

    const totalProteinEnergy = oralProteinEnergy + parenteralProteinEnergy;
    const totalCarbEnergy = oralCarbEnergy + parenteralCarbEnergy;
    const totalFatEnergy = oralFatEnergy + parenteralFatEnergy;
    const totalEnergy = totalProteinEnergy + totalCarbEnergy + totalFatEnergy;

    if (totalEnergy === 0) {
      return { proteinPercent: '0.0', carbPercent: '0.0', fatPercent: '0.0' };
    }

    return {
      proteinPercent: formatNumber((totalProteinEnergy / totalEnergy) * 100),
      carbPercent: formatNumber((totalCarbEnergy / totalEnergy) * 100),
      fatPercent: formatNumber((totalFatEnergy / totalEnergy) * 100)
    };
  };

  const grandTotalPercentages = calculateGrandTotalPercentages();

  // Nutritional status
  const nutritionalStatus = calculateNutritionalStatus(
    parseFloat(percentageOfTarget),
    {
      protein: grandTotalPercentages.proteinPercent,
      carbs: grandTotalPercentages.carbPercent,
      fat: grandTotalPercentages.fatPercent
    }
  );

  // Micronutrient calculations
  const calculateMicronutrients = () => {
    const oralMicro = {
      energy_kcal: oralTotals.energy,
      protein_g: oralTotals.protein,
      carbohydrate_g: oralTotals.carbs,
      fat_g: oralTotals.fat,
      fiber_g: 0,
      sodium_mg: 0,
      potassium_mg: 0,
      chloride_mg: 0,
      omega3_mg: 0,
      omega6_mg: 0,
      vitamin_a_mcg: 0,
      vitamin_d3_mcg: 0,
      vitamin_e_mcg: 0,
      vitamin_k_mcg: 0,
      vitamin_b1_mg: 0,
      vitamin_b2_mg: 0,
      vitamin_b3_mg: 0,
      vitamin_b5_mg: 0,
      vitamin_b6_mg: 0,
      vitamin_b12_mcg: 0,
      folic_acid_mcg: 0,
      biotin_mcg: 0,
      choline_mg: 0,
      vitamin_c_mg: 0,
      calcium_mg: 0,
      phosphorus_mg: 0,
      magnesium_mg: 0,
      iron_mg: 0,
      iodine_mcg: 0,
      zinc_mg: 0,
      selenium_mcg: 0,
      manganese_mg: 0,
      fluoride_mg: 0,
      chromium_mcg: 0,
      copper_mg: 0,
    };

    oralItems.forEach(item => {
      if (item.fiber_g !== undefined) oralMicro.fiber_g += item.fiber_g;
      if (item.sodium_mg !== undefined) oralMicro.sodium_mg += item.sodium_mg;
      if (item.potassium_mg !== undefined) oralMicro.potassium_mg += item.potassium_mg;
      if (item.chloride_mg !== undefined) oralMicro.chloride_mg += item.chloride_mg;
      if (item.omega3_mg !== undefined) oralMicro.omega3_mg += item.omega3_mg;
      if (item.omega6_mg !== undefined) oralMicro.omega6_mg += item.omega6_mg;
      if (item.vitamin_a_mcg !== undefined) oralMicro.vitamin_a_mcg += item.vitamin_a_mcg;
      if (item.vitamin_d3_mcg !== undefined) oralMicro.vitamin_d3_mcg += item.vitamin_d3_mcg;
      if (item.vitamin_e_mcg !== undefined) oralMicro.vitamin_e_mcg += item.vitamin_e_mcg;
      if (item.vitamin_k_mcg !== undefined) oralMicro.vitamin_k_mcg += item.vitamin_k_mcg;
      if (item.vitamin_b1_mg !== undefined) oralMicro.vitamin_b1_mg += item.vitamin_b1_mg;
      if (item.vitamin_b2_mg !== undefined) oralMicro.vitamin_b2_mg += item.vitamin_b2_mg;
      if (item.vitamin_b3_mg !== undefined) oralMicro.vitamin_b3_mg += item.vitamin_b3_mg;
      if (item.vitamin_b5_mg !== undefined) oralMicro.vitamin_b5_mg += item.vitamin_b5_mg;
      if (item.vitamin_b6_mg !== undefined) oralMicro.vitamin_b6_mg += item.vitamin_b6_mg;
      if (item.vitamin_b12_mcg !== undefined) oralMicro.vitamin_b12_mcg += item.vitamin_b12_mcg;
      if (item.folic_acid_mcg !== undefined) oralMicro.folic_acid_mcg += item.folic_acid_mcg;
      if (item.biotin_mcg !== undefined) oralMicro.biotin_mcg += item.biotin_mcg;
      if (item.choline_mg !== undefined) oralMicro.choline_mg += item.choline_mg;
      if (item.vitamin_c_mg !== undefined) oralMicro.vitamin_c_mg += item.vitamin_c_mg;
      if (item.calcium_mg !== undefined) oralMicro.calcium_mg += item.calcium_mg;
      if (item.phosphorus_mg !== undefined) oralMicro.phosphorus_mg += item.phosphorus_mg;
      if (item.magnesium_mg !== undefined) oralMicro.magnesium_mg += item.magnesium_mg;
      if (item.iron_mg !== undefined) oralMicro.iron_mg += item.iron_mg;
      if (item.iodine_mcg !== undefined) oralMicro.iodine_mcg += item.iodine_mcg;
      if (item.zinc_mg !== undefined) oralMicro.zinc_mg += item.zinc_mg;
      if (item.selenium_mcg !== undefined) oralMicro.selenium_mcg += item.selenium_mcg;
      if (item.manganese_mg !== undefined) oralMicro.manganese_mg += item.manganese_mg;
      if (item.fluoride_mg !== undefined) oralMicro.fluoride_mg += item.fluoride_mg;
      if (item.chromium_mcg !== undefined) oralMicro.chromium_mcg += item.chromium_mcg;
      if (item.copper_mg !== undefined) oralMicro.copper_mg += item.copper_mg;
    });

    const parenteralMicro = {
      energy_kcal: parenteralTotals.energy,
      protein_g: parenteralTotals.protein,
      carbohydrate_g: parenteralTotals.carbs,
      fat_g: parenteralTotals.fat,
      sodium_mmol: 0,
      potassium_mmol: 0,
      calcium_mmol: 0,
      chloride_mmol: 0,
      magnesium_mmol: 0,
      phosphate_mmol: 0,
      lactate_mmol: 0,
      acetate_meq: 0,
      nitrogen_g: 0,
    };

    parenteralItems.forEach(item => {
      if (item.sodium_mmol !== undefined) parenteralMicro.sodium_mmol += item.sodium_mmol;
      if (item.potassium_mmol !== undefined) parenteralMicro.potassium_mmol += item.potassium_mmol;
      if (item.calcium_mmol !== undefined) parenteralMicro.calcium_mmol += item.calcium_mmol;
      if (item.chloride_mmol !== undefined) parenteralMicro.chloride_mmol += item.chloride_mmol;
      if (item.magnesium_mmol !== undefined) parenteralMicro.magnesium_mmol += item.magnesium_mmol;
      if (item.phosphate_mmol !== undefined) parenteralMicro.phosphate_mmol += item.phosphate_mmol;
      if (item.lactate_mmol !== undefined) parenteralMicro.lactate_mmol += item.lactate_mmol;
      if (item.acetate_meq !== undefined) parenteralMicro.acetate_meq += item.acetate_meq;
      if (item.nitrogen_g !== undefined) parenteralMicro.nitrogen_g += item.nitrogen_g;
    });

    return { oral: oralMicro, parenteral: parenteralMicro };
  };

  const micronutrients = calculateMicronutrients();

  const sodiumMeq = {
    oral: micronutrients.oral.sodium_mg / 23,
    parenteral: micronutrients.parenteral.sodium_mmol,
    total: (micronutrients.oral.sodium_mg / 23) + micronutrients.parenteral.sodium_mmol
  };

  const potassiumMeq = {
    oral: micronutrients.oral.potassium_mg / 39.1,
    parenteral: micronutrients.parenteral.potassium_mmol,
    total: (micronutrients.oral.potassium_mg / 39.1) + micronutrients.parenteral.potassium_mmol
  };

  const handleAddOralItem = (item) => {
    const route = item.route || (activeInputTab === 'enteral' ? 'enteral' : (isEnteralItem(item) ? 'enteral' : 'oral'));
    setOralItems([...oralItems, { ...item, id: Date.now(), route }]);
  };

  const handleToggleRoute = (id) => {
    setOralItems(oralItems.map(item => {
      if (item.id === id) {
        const current = getItemRoute(item);
        const nextRoute = current === 'enteral' ? 'oral' : 'enteral';
        return { ...item, route: nextRoute };
      }
      return item;
    }));
  };

  const handleAddParenteralItem = (item) => {
    setParenteralItems([...parenteralItems, { ...item, id: Date.now(), route: 'parenteral' }]);
  };

  const handleRemoveOralItem = (id) => {
    setOralItems(oralItems.filter(item => item.id !== id));
  };

  const handleRemoveParenteralItem = (id) => {
    setParenteralItems(parenteralItems.filter(item => item.id !== id));
  };

  const handleEditOralItem = (item) => {
    setEditingItem({ ...item, type: 'oral' });
    setEditQuantity(item.quantity.toString());
    setEditRoute(getItemRoute(item));
    setShowEditDialog(true);
  };

  const handleEditParenteralItem = (item) => {
    setEditingItem({ ...item, type: 'parenteral' });
    setEditQuantity(item.quantity.toString());
    setShowEditDialog(true);
  };

  const handleSaveEdit = () => {
    if (!editQuantity || parseFloat(editQuantity) <= 0) {
      showAlert({
        type: 'warning',
        title: 'Jumlah Tidak Valid',
        message: 'Masukkan jumlah porsi yang lebih besar dari 0'
      });
      return;
    }

    const newQuantity = parseFloat(editQuantity);

    if (editingItem.type === 'oral') {
      setOralItems(oralItems.map(item => 
        item.id === editingItem.id 
          ? {
              ...item,
              route: editRoute,
              quantity: newQuantity,
              energy_kcal: (item.energy_kcal / item.quantity) * newQuantity,
              protein_g: (item.protein_g / item.quantity) * newQuantity,
              carbohydrate_g: (item.carbohydrate_g / item.quantity) * newQuantity,
              fat_g: (item.fat_g / item.quantity) * newQuantity,
            }
          : item
      ));
    } else {
      setParenteralItems(parenteralItems.map(item => 
        item.id === editingItem.id 
          ? {
              ...item,
              quantity: newQuantity,
              energy_kcal: (item.energy_kcal / item.quantity) * newQuantity,
              protein_g: (item.protein_g / item.quantity) * newQuantity,
              carbohydrate_g: (item.carbohydrate_g / item.quantity) * newQuantity,
              fat_g: (item.fat_g / item.quantity) * newQuantity,
            }
          : item
      ));
    }

    setShowEditDialog(false);
    setEditingItem(null);
    setEditQuantity('');
  };

  const handleCopyRecallSummary = () => {
    let summaryText = '';
    const isId = summaryLang === 'id';
    
    if (oralItems.length > 0 && parenteralItems.length > 0) {
      summaryText = isId ? 'Recall via Oral/Enteral dan Parenteral\n' : 'Recall via Oral/Enteral and Parenteral\n';
    } else if (oralItems.length > 0) {
      summaryText = 'Recall via Oral/Enteral\n';
    } else {
      summaryText = 'Recall via Parenteral\n';
    }
    
    const energyLabel = isId ? 'Energi' : 'Energy';
    const proteinLabel = isId ? 'Protein' : 'Protein';
    const carbsLabel = isId ? 'Karbohidrat' : 'Carbohydrate';
    const fatLabel = isId ? 'Lemak' : 'Fat';
    const targetLabel = isId ? 'dari target' : 'of target';
    
    if (oralItems.length > 0 && parenteralItems.length > 0) {
      summaryText += `${energyLabel}: ${Math.round(oralTotals.energy)} + ${Math.round(parenteralTotals.energy)} Kcal = ${Math.round(grandTotal.energy)} Kcal (${percentageOfTarget}% ${targetLabel} ${targetEnergy}Kcal)\n`;
    } else if (oralItems.length > 0) {
      summaryText += `${energyLabel}: ${Math.round(oralTotals.energy)} Kcal (${oralPercentages.energyPercent}% ${targetLabel})\n`;
    } else {
      summaryText += `${energyLabel}: ${Math.round(parenteralTotals.energy)} Kcal (${parenteralPercentages.energyPercent}% ${targetLabel})\n`;
    }
    
    if (oralItems.length > 0 && parenteralItems.length > 0) {
      summaryText += `${proteinLabel}: ${formatNumber(oralTotals.protein)}g + ${formatNumber(parenteralTotals.protein)}g = ${formatNumber(grandTotal.protein)}g (${grandTotalPercentages.proteinPercent}%)\n`;
    } else if (oralItems.length > 0) {
      summaryText += `${proteinLabel}: ${formatNumber(oralTotals.protein)}g (${oralPercentages.proteinPercent}%)\n`;
    } else {
      summaryText += `${proteinLabel}: ${formatNumber(parenteralTotals.protein)}g (${parenteralPercentages.proteinPercent}%)\n`;
    }
    
    if (oralItems.length > 0 && parenteralItems.length > 0) {
      summaryText += `${carbsLabel}: ${formatNumber(oralTotals.carbs)}g + ${formatNumber(parenteralTotals.carbs)}g = ${formatNumber(grandTotal.carbs)}g (${grandTotalPercentages.carbPercent}%)\n`;
    } else if (oralItems.length > 0) {
      summaryText += `${carbsLabel}: ${formatNumber(oralTotals.carbs)}g (${oralPercentages.carbPercent}%)\n`;
    } else {
      summaryText += `${carbsLabel}: ${formatNumber(parenteralTotals.carbs)}g (${parenteralPercentages.carbPercent}%)\n`;
    }
    
    if (oralItems.length > 0 && parenteralItems.length > 0) {
      summaryText += `${fatLabel}: ${formatNumber(oralTotals.fat)}g + ${formatNumber(parenteralTotals.fat)}g = ${formatNumber(grandTotal.fat)}g (${grandTotalPercentages.fatPercent}%)\n`;
    } else if (oralItems.length > 0) {
      summaryText += `${fatLabel}: ${formatNumber(oralTotals.fat)}g (${oralPercentages.fatPercent}%)\n`;
    } else {
      summaryText += `${fatLabel}: ${formatNumber(parenteralTotals.fat)}g (${parenteralPercentages.fatPercent}%)\n`;
    }
    
    const sodiumLabel = isId ? 'Natrium (Na)' : 'Sodium (Na)';
    const potassiumLabel = isId ? 'Kalium (K)' : 'Potassium (K)';
    
    if (oralItems.length > 0 && parenteralItems.length > 0) {
      summaryText += `${sodiumLabel}: ${formatNumber(sodiumMeq.oral)} mEq + ${formatNumber(sodiumMeq.parenteral)} mEq = ${formatNumber(sodiumMeq.total)} mEq\n`;
    } else if (oralItems.length > 0) {
      summaryText += `${sodiumLabel}: ${formatNumber(sodiumMeq.oral)} mEq\n`;
    } else {
      summaryText += `${sodiumLabel}: ${formatNumber(sodiumMeq.parenteral)} mEq\n`;
    }
    
    if (oralItems.length > 0 && parenteralItems.length > 0) {
      summaryText += `${potassiumLabel}: ${formatNumber(potassiumMeq.oral)} mEq + ${formatNumber(potassiumMeq.parenteral)} mEq = ${formatNumber(potassiumMeq.total)} mEq\n`;
    } else if (oralItems.length > 0) {
      summaryText += `${potassiumLabel}: ${formatNumber(potassiumMeq.oral)} mEq\n`;
    } else {
      summaryText += `${potassiumLabel}: ${formatNumber(potassiumMeq.parenteral)} mEq\n`;
    }

    summaryText += `Rute: Oral ${routeSplits.oralKcal} Kcal (${routeSplits.oralPct}%) | Enteral ${routeSplits.enteralKcal} Kcal (${routeSplits.enteralPct}%) | Parenteral ${routeSplits.parenteralKcal} Kcal (${routeSplits.parenteralPct}%)`;
    
    navigator.clipboard.writeText(summaryText).then(() => {
      toast.success(
        isId ? 'Tersalin ke Clipboard!' : 'Copied to Clipboard!',
        isId ? 'Ringkasan Recall berhasil disalin ke clipboard' : 'Recall summary copied to clipboard'
      );
    }).catch(() => {
      toast.error(
        isId ? 'Gagal Menyalin' : 'Copy Failed',
        isId ? 'Gagal menyalin ringkasan ke clipboard' : 'Failed to copy to clipboard'
      );
    });
  };

  const handleSave = async () => {
    if (!patientId) {
      showAlert({
        type: 'warning',
        title: 'Pasien Tidak Dipilih',
        message: 'Pilih pasien terlebih dahulu sebelum menyimpan perhitungan'
      });
      return;
    }

    if (oralItems.length === 0 && parenteralItems.length === 0) {
      showAlert({
        type: 'warning',
        title: 'Item Kosong',
        message: 'Silakan tambahkan minimal satu makanan oral/enteral atau produk parenteral'
      });
      return;
    }

    const formatRecordItems = (items) => {
      return items.map(item => ({
        item_id: item.item_id || (typeof item.id === 'string' ? item.id : null),
        name: item.name || 'Item',
        quantity: parseFloat(item.quantity) || 1,
        unit: item.unit || 'portion',
        energy_kcal: parseFloat(item.energy_kcal) || 0,
        protein_g: parseFloat(item.protein_g) || 0,
        carbohydrate_g: parseFloat(item.carbohydrate_g) || 0,
        fat_g: parseFloat(item.fat_g) || 0,
      }));
    };

    const recordData = {
      patient_id: patientId || null,
      patient_name: patient?.name || 'Pasien',
      date,
      target_energy_kcal: parseFloat(targetEnergy) || 1600,
      oral_enteral_items: formatRecordItems(oralItems),
      parenteral_items: formatRecordItems(parenteralItems),
    };

    setSaving(true);
    try {
      await recordsAPI.create(recordData);
      toast.success(
        'Berhasil Disimpan',
        'Hasil perhitungan recall nutrisi berhasil disimpan ke riwayat pasien'
      );
      // Refresh patient data to update record counters
      if (patientId) {
        try {
          const freshPatient = await patientsAPI.getById(patientId);
          setPatient(freshPatient);
        } catch (e) {
          console.error('Failed to reload patient:', e);
        }
      }
    } catch (error) {
      console.error('Failed to save record:', error);
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Gagal menyimpan data perhitungan';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      const isRecordLimit = errorMessage.includes('Maximum limit') || errorMessage.includes('record limit') || errorMessage.includes('calculation limit');
      showAlert({
        type: 'danger',
        title: isRecordLimit ? 'Batas Rekor Tercapai' : 'Gagal Menyimpan',
        message: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (oralItems.length === 0 && parenteralItems.length === 0) {
      showAlert({
        type: 'warning',
        title: 'Item Kosong',
        message: 'Tambahkan item makanan atau parenteral sebelum mengekspor PDF'
      });
      return;
    }

    setExporting(true);
    try {
      await generateCurrentCalculationPDF(
        patient || { name: 'Pasien' },
        date,
        targetEnergy,
        oralItems,
        parenteralItems,
        oralTotals,
        parenteralTotals,
        grandTotal
      );
    } catch (error) {
      console.error('PDF export error:', error);
      showAlert({
        type: 'danger',
        title: 'Gagal Ekspor PDF',
        message: 'Gagal mengekspor PDF: ' + error.message
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = () => {
    if (!patient) {
      showAlert({
        type: 'warning',
        title: 'Data Pasien Tidak Ditemukan',
        message: 'Informasi pasien belum dimuat'
      });
      return;
    }

    if (oralItems.length === 0 && parenteralItems.length === 0) {
      showAlert({
        type: 'warning',
        title: 'Item Kosong',
        message: 'Tambahkan minimal satu item sebelum mengekspor data ke Excel'
      });
      return;
    }

    try {
      const filename = `NUTRIMUS_Macro-Micronutrients_${patient.name}_${date}.xlsx`;
      exportMicronutrientsToExcel(
        {
          patient,
          date,
          targetEnergy,
          micronutrients,
          sodiumMeq,
          potassiumMeq
        },
        filename
      );
      
      toast.success(
        'Ekspor Excel Berhasil',
        'Data makronutrien dan mikronutrien berhasil diunduh dalam format Excel'
      );
    } catch (error) {
      console.error('Excel export error:', error);
      showAlert({
        type: 'danger',
        title: 'Gagal Ekspor Excel',
        message: 'Gagal mengekspor Excel: ' + error.message
      });
    }
  };

  // Salin Recall dari hari sebelumnya
  const handleCopyFromPrevious = async () => {
    if (!patientId) return;
    setLoadingCopy(true);
    try {
      const records = await patientsAPI.getRecords(patientId);
      if (!records || records.length === 0) {
        showAlert({ type: 'warning', title: 'Tidak Ada Riwayat', message: 'Belum ada recall tersimpan untuk pasien ini.' });
        return;
      }
      // Sort by date descending, pick most recent that is NOT today
      const sorted = [...records].sort((a, b) => new Date(b.date) - new Date(a.date));
      const previous = sorted.find(r => r.date !== date) || sorted[0];
      if (!previous) {
        showAlert({ type: 'warning', title: 'Tidak Ditemukan', message: 'Tidak ada recall dari hari sebelumnya.' });
        return;
      }
      // Populate items from previous record
      const newOral = (previous.oral_enteral_items || []).map(item => ({ ...item, id: Date.now() + Math.random() }));
      const newParenteral = (previous.parenteral_items || []).map(item => ({ ...item, id: Date.now() + Math.random() }));
      setOralItems(newOral);
      setParenteralItems(newParenteral);
      if (previous.target_energy_kcal) setTargetEnergy(previous.target_energy_kcal);
      toast.success(
        'Recall Disalin!',
        `Data recall dari ${new Date(previous.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })} berhasil disalin ke hari ini.`
      );
    } catch (error) {
      console.error('Copy recall error:', error);
      showAlert({ type: 'danger', title: 'Gagal Menyalin', message: 'Terjadi kesalahan saat mengambil data recall sebelumnya.' });
    } finally {
      setLoadingCopy(false);
    }
  };

  const handleClearAll = () => {
    confirm({
      type: 'danger',
      title: 'Kosongkan Seluruh Data',
      message: 'Apakah Anda yakin ingin menghapus semua item oral/enteral dan parenteral yang sudah dimasukkan?',
      confirmText: 'Ya, Kosongkan',
      cancelText: 'Batal',
      onConfirm: () => {
        setOralItems([]);
        setParenteralItems([]);
      }
    });
  };

  // Handle items added via Voice Input (Oral / Enteral)
  const handleAddVoiceItems = (items) => {
    const withIds = items.map(item => ({
      ...item,
      id: Date.now() + Math.random(),
      route: activeInputTab === 'enteral' ? 'enteral' : (item.route || (isEnteralItem(item) ? 'enteral' : 'oral'))
    }));
    setOralItems(prev => [...prev, ...withIds]);
    toast.success(
      'Ditambahkan via Suara',
      `${withIds.length} item ${activeInputTab === 'enteral' ? 'formula enteral' : 'makanan oral'} berhasil ditambahkan dari input suara.`
    );
  };

  // Handle items added via Voice Input (Parenteral)
  const handleAddVoiceParenteralItems = (items) => {
    const withIds = items.map(item => ({ ...item, id: Date.now() + Math.random() }));
    setParenteralItems(prev => [...prev, ...withIds]);
    toast.success(
      'Ditambahkan via Suara',
      `${withIds.length} produk parenteral berhasil ditambahkan dari input suara.`
    );
  };

  const pieData = [
    { name: 'Protein', value: parseFloat(grandTotalPercentages.proteinPercent) || 0, color: '#10b981' },
    { name: 'Karbohidrat', value: parseFloat(grandTotalPercentages.carbPercent) || 0, color: '#8b5cf6' },
    { name: 'Lemak', value: parseFloat(grandTotalPercentages.fatPercent) || 0, color: '#eab308' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col justify-between">
      <div>
        {/* Top Slim Navbar */}
        <Navbar 
          user={user} 
          onLogout={onLogout} 
          breadcrumbs={[
            { label: 'Daftar Pasien', href: '/patients' },
            { label: patient?.name || 'Pasien' },
            { label: 'Kalkulasi Recall' }
          ]}
        />

        {/* Main Workspace */}
        <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6 pb-28 lg:pb-8">
          
          {/* Top Patient Header Banner */}
          <div className="bg-white rounded-2xl p-3.5 sm:p-5 border border-slate-200 shadow-xs">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 sm:gap-4">
              {/* Patient Basic Info */}
              <div className="flex items-center gap-3 sm:gap-4">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => navigate('/patients')}
                  className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl border-slate-200 hover:bg-slate-100 flex-shrink-0"
                  title="Kembali ke Daftar Pasien"
                >
                  <ArrowLeft className="h-4 w-4 text-slate-600" />
                </Button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">
                      {patient ? patient.name : 'Memuat Pasien...'}
                    </h1>
                    {patient && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {patient.gender === 'male' ? 'Laki-laki' : patient.gender === 'female' ? 'Perempuan' : '-'}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500 mt-0.5 sm:mt-1">
                    <span>Usia: <strong>{patient?.age || '-'} tahun</strong></span>
                    <span>•</span>
                    <span>Total Rekaman: <strong>{patient?.total_records || 0} recall</strong></span>
                  </div>
                </div>
              </div>

              {/* Recall Parameters & History Link */}
              <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-3 bg-slate-50/80 p-2.5 sm:p-3 rounded-xl border border-slate-200/80">
                <div className="col-span-1 sm:col-auto flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <Label htmlFor="recall-date" className="text-[11px] sm:text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Tanggal:
                  </Label>
                  <Input
                    id="recall-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="h-9 text-xs w-full sm:w-36 bg-white border-slate-200 rounded-lg shadow-2xs"
                  />
                </div>

                <div className="col-span-1 sm:col-auto flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <Label htmlFor="target-energy" className="text-[11px] sm:text-xs font-semibold text-slate-600 whitespace-nowrap">
                    Target Energi:
                  </Label>
                  <div className="relative w-full sm:w-28">
                    <Input
                      id="target-energy"
                      type="number"
                      value={targetEnergy}
                      onChange={(e) => setTargetEnergy(Number(e.target.value))}
                      placeholder="1600"
                      className="h-9 text-xs w-full pr-11 bg-white border-slate-200 font-semibold text-slate-800 rounded-lg shadow-2xs"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-bold pointer-events-none">
                      Kcal
                    </span>
                  </div>
                </div>

                {patientId && (
                  <div className="col-span-2 sm:col-auto sm:ml-auto flex items-center gap-2 w-full sm:w-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/patients/${patientId}/profile`)}
                      className="h-9 text-xs font-semibold bg-white border-slate-200 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 text-slate-700 flex items-center justify-center gap-1.5 rounded-lg shadow-2xs transition-colors flex-1 sm:flex-none"
                      title="Profil Ringkasan Pasien"
                    >
                      <User className="h-3.5 w-3.5 text-purple-500" />
                      <span className="hidden sm:inline">Profil</span>
                      <span className="sm:hidden">Profil Pasien</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/patients/${patientId}/history`)}
                      className="h-9 text-xs font-semibold bg-white border-slate-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 text-slate-700 flex items-center justify-center gap-1.5 rounded-lg shadow-2xs transition-colors flex-1 sm:flex-none"
                    >
                      <HistoryIcon className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="hidden sm:inline">Riwayat</span>
                      <span className="sm:hidden">Riwayat Recall</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 2-Column Split Workspace (Desktop) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            
            {/* LEFT COLUMN: Input Workspace & Deep Analysis (7 cols) */}
            <div className="lg:col-span-7 space-y-6">
              
              {/* Nutrition Intake Input Card */}
              <Card className="border border-slate-200 shadow-xs overflow-hidden bg-white">
                {/* Segmented Tab Controls: Oral vs Enteral vs Parenteral */}
                <div className="border-b border-slate-200 bg-slate-50/70 p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                  <div className="flex items-center bg-slate-200/80 p-1 rounded-xl w-full sm:w-auto">
                    <button
                      onClick={() => setActiveInputTab('oral')}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        activeInputTab === 'oral'
                          ? 'bg-white text-emerald-800 shadow-xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <span>🥗 Oral</span>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                        activeInputTab === 'oral' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-300/80 text-slate-700'
                      }`}>
                        {routeSplits.itemCounts.oral}
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveInputTab('enteral')}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        activeInputTab === 'enteral'
                          ? 'bg-white text-sky-800 shadow-xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <span>🥣 Enteral</span>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                        activeInputTab === 'enteral' ? 'bg-sky-100 text-sky-800' : 'bg-slate-300/80 text-slate-700'
                      }`}>
                        {routeSplits.itemCounts.enteral}
                      </span>
                    </button>

                    <button
                      onClick={() => setActiveInputTab('parenteral')}
                      className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        activeInputTab === 'parenteral'
                          ? 'bg-white text-purple-800 shadow-xs'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      <span>💉 Parenteral</span>
                      <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                        activeInputTab === 'parenteral' ? 'bg-purple-100 text-purple-800' : 'bg-slate-300/80 text-slate-700'
                      }`}>
                        {parenteralItems.length}
                      </span>
                    </button>
                  </div>

                  {/* Add Item Button + Voice Input per Tab */}
                  {activeInputTab === 'oral' ? (
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button 
                        size="sm" 
                        onClick={() => setShowFoodDialog(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 sm:h-8 shadow-xs flex-1 sm:flex-none justify-center"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Tambah Makanan
                      </Button>
                      <VoiceRecallInput key="voice-oral" type="oral" onAddItems={handleAddVoiceItems} />
                    </div>
                  ) : activeInputTab === 'enteral' ? (
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button 
                        size="sm" 
                        onClick={() => setShowFoodDialog(true)}
                        className="bg-sky-600 hover:bg-sky-700 text-white text-xs h-9 sm:h-8 shadow-xs flex-1 sm:flex-none justify-center"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Tambah Formula Enteral
                      </Button>
                      <VoiceRecallInput key="voice-enteral" type="oral" onAddItems={handleAddVoiceItems} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button 
                        size="sm" 
                        onClick={() => setShowParenteralDialog(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-9 sm:h-8 shadow-xs flex-1 sm:flex-none justify-center"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Tambah Produk
                      </Button>
                      <VoiceRecallInput key="voice-parenteral" type="parenteral" onAddItems={handleAddVoiceParenteralItems} />
                    </div>
                  )}
                </div>

                {/* Items Content List */}
                <CardContent className="p-4 sm:p-5">
                  {/* Copy from Previous - Smart Prompt when empty */}
                  {oralItems.length === 0 && parenteralItems.length === 0 && patientId && (
                    <div className="mb-4 p-3 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Copy className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                        <span className="text-xs text-indigo-800 font-medium">
                          Salin seluruh recall dari hari sebelumnya sebagai titik awal?
                        </span>
                      </div>
                      <button
                        onClick={handleCopyFromPrevious}
                        disabled={loadingCopy}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
                      >
                        {loadingCopy ? (
                          <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                        <span>{loadingCopy ? 'Menyalin...' : 'Salin Recall'}</span>
                      </button>
                    </div>
                  )}

                  {activeInputTab === 'oral' ? (
                    /* Oral Items List */
                    <div>
                      {displayedOralItems.length === 0 ? (
                        <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
                          <div className="w-10 h-10 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-2">
                            <Plus className="h-5 w-5" />
                          </div>
                          <p className="text-sm font-semibold text-slate-700">Belum ada makanan oral</p>
                          <p className="text-xs text-slate-400 mt-0.5">Klik "Tambah Makanan" untuk menambahkan asupan via rute oral</p>
                        </div>
                      ) : (
                        <div className="space-y-2.5">
                          {displayedOralItems.map((item) => (
                            <div 
                              key={item.id} 
                              className="p-3 rounded-xl border border-slate-200/90 bg-white hover:border-emerald-300 hover:shadow-xs transition-all space-y-2"
                            >
                              {/* Row 1: Name, Quantity & Action Buttons */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-bold text-sm text-slate-900 leading-snug break-words">{item.name}</span>
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/60 flex-shrink-0">
                                      {item.quantity} × {item.unit}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-0.5 flex-shrink-0 -mr-1 -mt-0.5">
                                  <button
                                    onClick={() => handleEditOralItem(item)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="Edit Porsi"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveOralItem(item.id)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Hapus Item"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Row 2: Route Toggle & Macro Nutrients */}
                              <div className="flex items-center justify-between flex-wrap gap-2 pt-1.5 border-t border-slate-100">
                                <button
                                  type="button"
                                  onClick={() => handleToggleRoute(item.id)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 transition-all active:scale-95"
                                  title="Klik untuk ubah rute ke Enteral"
                                >
                                  <span>🥗 Oral</span>
                                  <span className="text-[9px] opacity-70">⇄ Enteral</span>
                                </button>
                                <div className="flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                                  <span className="font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">
                                    {Math.round(item.energy_kcal)} Kcal
                                  </span>
                                  <span>P: <strong className="text-slate-800">{formatNumber(item.protein_g)}</strong>g</span>
                                  <span className="text-slate-300">•</span>
                                  <span>KH: <strong className="text-slate-800">{formatNumber(item.carbohydrate_g)}</strong>g</span>
                                  <span className="text-slate-300">•</span>
                                  <span>L: <strong className="text-slate-800">{formatNumber(item.fat_g)}</strong>g</span>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Oral Subtotal Bar */}
                          <div className="mt-4 p-3 bg-emerald-50/60 rounded-xl border border-emerald-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            <span className="font-bold text-emerald-900 flex items-center gap-1.5">
                              <span>🥗</span> Subtotal Rute Oral:
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-emerald-800">
                              <span>{routeSplits.oralKcal} Kcal ({routeSplits.oralPct}%)</span>
                              <span>P: {formatNumber(routeSplits.oralProtein)}g</span>
                              <span>KH: {formatNumber(routeSplits.oralCarbs)}g</span>
                              <span>L: {formatNumber(routeSplits.oralFat)}g</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : activeInputTab === 'enteral' ? (
                    /* Enteral Items List */
                    <div>
                      {displayedEnteralItems.length === 0 ? (
                        <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
                          <div className="w-10 h-10 mx-auto rounded-full bg-sky-50 text-sky-600 flex items-center justify-center mb-2">
                            <Plus className="h-5 w-5" />
                          </div>
                          <p className="text-sm font-semibold text-slate-700">Belum ada formula enteral / sonde</p>
                          <p className="text-xs text-slate-400 mt-0.5">Klik "Tambah Formula Enteral" untuk menambahkan formula cair via NGT/OGT/PEG</p>
                        </div>
                      ) : (
                        <div className="space-y-2.5">
                          {displayedEnteralItems.map((item) => (
                            <div 
                              key={item.id} 
                              className="p-3 rounded-xl border border-slate-200/90 bg-white hover:border-sky-300 hover:shadow-xs transition-all space-y-2"
                            >
                              {/* Row 1: Name, Quantity & Action Buttons */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-bold text-sm text-slate-900 leading-snug break-words">{item.name}</span>
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-200/60 flex-shrink-0">
                                      {item.quantity} × {item.unit}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-0.5 flex-shrink-0 -mr-1 -mt-0.5">
                                  <button
                                    onClick={() => handleEditOralItem(item)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="Edit Porsi"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveOralItem(item.id)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Hapus Item"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Row 2: Route Toggle & Macro Nutrients */}
                              <div className="flex items-center justify-between flex-wrap gap-2 pt-1.5 border-t border-slate-100">
                                <button
                                  type="button"
                                  onClick={() => handleToggleRoute(item.id)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold border bg-sky-50 text-sky-700 border-sky-200 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-all active:scale-95"
                                  title="Klik untuk ubah rute ke Oral"
                                >
                                  <span>🥣 Enteral</span>
                                  <span className="text-[9px] opacity-70">⇄ Oral</span>
                                </button>
                                <div className="flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                                  <span className="font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">
                                    {Math.round(item.energy_kcal)} Kcal
                                  </span>
                                  <span>P: <strong className="text-slate-800">{formatNumber(item.protein_g)}</strong>g</span>
                                  <span className="text-slate-300">•</span>
                                  <span>KH: <strong className="text-slate-800">{formatNumber(item.carbohydrate_g)}</strong>g</span>
                                  <span className="text-slate-300">•</span>
                                  <span>L: <strong className="text-slate-800">{formatNumber(item.fat_g)}</strong>g</span>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Enteral Subtotal Bar */}
                          <div className="mt-4 p-3 bg-sky-50/60 rounded-xl border border-sky-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            <span className="font-bold text-sky-900 flex items-center gap-1.5">
                              <span>🥣</span> Subtotal Rute Enteral:
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-sky-800">
                              <span>{routeSplits.enteralKcal} Kcal ({routeSplits.enteralPct}%)</span>
                              <span>P: {formatNumber(routeSplits.enteralProtein)}g</span>
                              <span>KH: {formatNumber(routeSplits.enteralCarbs)}g</span>
                              <span>L: {formatNumber(routeSplits.enteralFat)}g</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Parenteral Items List */
                    <div>
                      {parenteralItems.length === 0 ? (
                        <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
                          <div className="w-10 h-10 mx-auto rounded-full bg-purple-50 text-purple-600 flex items-center justify-center mb-2">
                            <Plus className="h-5 w-5" />
                          </div>
                          <p className="text-sm font-semibold text-slate-700">Belum ada produk parenteral</p>
                          <p className="text-xs text-slate-400 mt-0.5">Klik tombol "Tambah Produk" untuk memilih cairan infus/parenteral</p>
                        </div>
                      ) : (
                        <div className="space-y-2.5">
                          {parenteralItems.map((item) => (
                            <div 
                              key={item.id} 
                              className="p-3 rounded-xl border border-slate-200/90 bg-white hover:border-purple-300 hover:shadow-xs transition-all space-y-2"
                            >
                              {/* Row 1: Name, Quantity & Action Buttons */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-bold text-sm text-slate-900 leading-snug break-words">{item.name}</span>
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200/60 flex-shrink-0">
                                      {item.quantity} × {item.unit}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-0.5 flex-shrink-0 -mr-1 -mt-0.5">
                                  <button
                                    onClick={() => handleEditParenteralItem(item)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="Edit Jumlah"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveParenteralItem(item.id)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="Hapus Item"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Row 2: Badge Rute & Macro Nutrients */}
                              <div className="flex items-center justify-between flex-wrap gap-2 pt-1.5 border-t border-slate-100">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold border bg-purple-50 text-purple-700 border-purple-200">
                                  💉 Parenteral
                                </span>
                                <div className="flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                                  <span className="font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">
                                    {Math.round(item.energy_kcal)} Kcal
                                  </span>
                                  <span>P: <strong className="text-slate-800">{formatNumber(item.protein_g)}</strong>g</span>
                                  <span className="text-slate-300">•</span>
                                  <span>KH: <strong className="text-slate-800">{formatNumber(item.carbohydrate_g)}</strong>g</span>
                                  <span className="text-slate-300">•</span>
                                  <span>L: <strong className="text-slate-800">{formatNumber(item.fat_g)}</strong>g</span>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Parenteral Subtotal Bar */}
                          <div className="mt-4 p-3 bg-purple-50/60 rounded-xl border border-purple-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            <span className="font-bold text-purple-900 flex items-center gap-1.5">
                              <span>💉</span> Subtotal Rute Parenteral:
                            </span>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-purple-800">
                              <span>{routeSplits.parenteralKcal} Kcal ({routeSplits.parenteralPct}%)</span>
                              <span>P: {formatNumber(routeSplits.parenteralProtein)}g</span>
                              <span>KH: {formatNumber(routeSplits.parenteralCarbs)}g</span>
                              <span>L: {formatNumber(routeSplits.parenteralFat)}g</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Analysis & Details Tabs (CPPT Text & Micronutrients) */}
              {(oralItems.length > 0 || parenteralItems.length > 0) && (
                <Card className="border border-slate-200 shadow-xs overflow-hidden bg-white">
                  <div className="border-b border-slate-200 bg-slate-50/70 p-2 sm:p-3 flex items-center justify-between">
                    <div className="flex items-center gap-1 bg-slate-200/80 p-1 rounded-xl">
                      <button
                        onClick={() => setAnalysisTab('summary')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          analysisTab === 'summary'
                            ? 'bg-white text-slate-800 shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        📋 Ringkasan CPPT / Teks
                      </button>
                      <button
                        onClick={() => setAnalysisTab('micronutrients')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          analysisTab === 'micronutrients'
                            ? 'bg-white text-slate-800 shadow-xs'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        🔬 Detail Mikronutrien &amp; Elektrolit
                      </button>
                    </div>

                    {analysisTab === 'micronutrients' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportExcel}
                        className="h-8 text-xs gap-1.5 bg-white border-slate-200 text-emerald-700 hover:bg-emerald-50"
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Export Excel</span>
                      </Button>
                    )}
                  </div>

                  <CardContent className="p-4 sm:p-5">
                    {analysisTab === 'summary' ? (
                      /* CPPT / Text Summary View */
                      <div className="space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500">Bahasa:</span>
                            <div className="flex rounded-md border border-slate-200 overflow-hidden text-xs">
                              <button 
                                onClick={() => setSummaryLang('id')}
                                className={`px-2.5 py-1 ${summaryLang === 'id' ? 'bg-emerald-600 text-white font-medium' : 'bg-white text-slate-600'}`}
                              >
                                ID
                              </button>
                              <button 
                                onClick={() => setSummaryLang('en')}
                                className={`px-2.5 py-1 ${summaryLang === 'en' ? 'bg-emerald-600 text-white font-medium' : 'bg-white text-slate-600'}`}
                              >
                                EN
                              </button>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCopyRecallSummary}
                            className="h-8 text-xs gap-1.5 text-slate-700 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            <Copy className="h-3.5 w-3.5 text-emerald-600" />
                            Salin Format CPPT
                          </Button>
                        </div>

                        <div className="bg-slate-50 p-4 rounded-xl text-xs font-mono text-slate-800 space-y-1.5 border border-slate-200 leading-relaxed select-all">
                          <p className="font-bold text-slate-900">
                            {oralItems.length > 0 && parenteralItems.length > 0
                              ? (summaryLang === 'id' ? 'Recall via Oral/Enteral dan Parenteral' : 'Recall via Oral/Enteral and Parenteral')
                              : oralItems.length > 0
                                ? 'Recall via Oral/Enteral'
                                : 'Recall via Parenteral'}
                          </p>
                          <p>
                            {summaryLang === 'id' ? 'Energi' : 'Energy'}: {Math.round(grandTotal.energy)} Kcal ({percentageOfTarget}% {summaryLang === 'id' ? 'dari target' : 'of target'} {targetEnergy} Kcal)
                          </p>
                          <p>
                            Protein: {formatNumber(grandTotal.protein)}g ({grandTotalPercentages.proteinPercent}%)
                          </p>
                          <p>
                            {summaryLang === 'id' ? 'Karbohidrat' : 'Carbohydrate'}: {formatNumber(grandTotal.carbs)}g ({grandTotalPercentages.carbPercent}%)
                          </p>
                          <p>
                            {summaryLang === 'id' ? 'Lemak' : 'Fat'}: {formatNumber(grandTotal.fat)}g ({grandTotalPercentages.fatPercent}%)
                          </p>
                          <p>
                            {summaryLang === 'id' ? 'Natrium' : 'Sodium'} (Na): {formatNumber(sodiumMeq.total)} mEq
                          </p>
                          <p>
                            {summaryLang === 'id' ? 'Kalium' : 'Potassium'} (K): {formatNumber(potassiumMeq.total)} mEq
                          </p>
                          <p className="text-emerald-800 font-semibold pt-1 border-t border-slate-200">
                            {summaryLang === 'id' ? 'Rute' : 'Route'}: 🥗 Oral {routeSplits.oralKcal} Kcal ({routeSplits.oralPct}%) • 🥣 Enteral {routeSplits.enteralKcal} Kcal ({routeSplits.enteralPct}%) • 💉 Parenteral {routeSplits.parenteralKcal} Kcal ({routeSplits.parenteralPct}%)
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* Micronutrients Breakdown */
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Oral/Enteral Micronutrients Table */}
                          <div className="border border-slate-200 rounded-xl overflow-hidden">
                            <div className="bg-emerald-100/80 text-emerald-900 font-bold px-3 py-2 text-xs text-center">
                              ORAL / ENTERAL KOMPOSISI
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                              <table className="w-full text-xs">
                                <tbody className="divide-y divide-slate-100">
                                  <tr className="bg-emerald-50/30"><td className="px-3 py-1.5 text-slate-600">Energi</td><td className="px-3 py-1.5 text-right font-semibold">{formatKcal(micronutrients.oral.energy_kcal)}</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Protein</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.protein_g)} g</td></tr>
                                  <tr className="bg-emerald-50/30"><td className="px-3 py-1.5 text-slate-600">Karbohidrat</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.carbohydrate_g)} g</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Lemak</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.fat_g)} g</td></tr>
                                  <tr className="bg-emerald-50/30"><td className="px-3 py-1.5 text-slate-600">Serat (Fiber)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.fiber_g)} g</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Natrium (Na)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.sodium_mg)} mg</td></tr>
                                  <tr className="bg-emerald-50/30"><td className="px-3 py-1.5 text-slate-600">Kalium (K)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.potassium_mg)} mg</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Kalsium (Ca)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.calcium_mg)} mg</td></tr>
                                  <tr className="bg-emerald-50/30"><td className="px-3 py-1.5 text-slate-600">Besi (Fe)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.iron_mg)} mg</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Seng (Zn)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.zinc_mg)} mg</td></tr>
                                  <tr className="bg-emerald-50/30"><td className="px-3 py-1.5 text-slate-600">Vitamin C</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.vitamin_c_mg)} mg</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Vitamin A</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.vitamin_a_mcg)} mcg</td></tr>
                                  <tr className="bg-emerald-50/30"><td className="px-3 py-1.5 text-slate-600">Vitamin D3</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.oral.vitamin_d3_mcg)} mcg</td></tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Parenteral Micronutrients Table */}
                          <div className="border border-slate-200 rounded-xl overflow-hidden">
                            <div className="bg-sky-100/70 text-sky-900 font-bold px-3 py-2 text-xs text-center">
                              PARENTERAL KOMPOSISI
                            </div>
                            <div className="max-h-80 overflow-y-auto">
                              <table className="w-full text-xs">
                                <tbody className="divide-y divide-slate-100">
                                  <tr className="bg-sky-50/40"><td className="px-3 py-1.5 text-slate-600">Energi</td><td className="px-3 py-1.5 text-right font-semibold">{formatKcal(micronutrients.parenteral.energy_kcal)}</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Protein</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.protein_g)} g</td></tr>
                                  <tr className="bg-sky-50/40"><td className="px-3 py-1.5 text-slate-600">Karbohidrat</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.carbohydrate_g)} g</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Lemak</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.fat_g)} g</td></tr>
                                  <tr className="bg-sky-50/40"><td className="px-3 py-1.5 text-slate-600">Natrium (Na)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.sodium_mmol)} mmol</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Kalium (K)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.potassium_mmol)} mmol</td></tr>
                                  <tr className="bg-sky-50/40"><td className="px-3 py-1.5 text-slate-600">Kalsium (Ca)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.calcium_mmol)} mmol</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Klorida (Cl)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.chloride_mmol)} mmol</td></tr>
                                  <tr className="bg-sky-50/40"><td className="px-3 py-1.5 text-slate-600">Magnesium (Mg)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.magnesium_mmol)} mmol</td></tr>
                                  <tr><td className="px-3 py-1.5 text-slate-600">Fosfat (Phosphate)</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.phosphate_mmol)} mmol</td></tr>
                                  <tr className="bg-sky-50/40"><td className="px-3 py-1.5 text-slate-600">Nitrogen</td><td className="px-3 py-1.5 text-right font-semibold">{formatNumber(micronutrients.parenteral.nitrogen_g)} g</td></tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* RIGHT COLUMN: Sticky Live Summary & Quick Actions (5 cols) */}
            <div className="lg:col-span-5 lg:sticky lg:top-20 space-y-4">
              
              {/* Grand Total Hero Card */}
              <Card className="border border-slate-200 shadow-md bg-white overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-5 py-4 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-emerald-100 uppercase tracking-wider">Total Asupan Harian</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-3xl font-extrabold tracking-tight">
                          {Math.round(grandTotal.energy)}
                        </span>
                        <span className="text-sm font-medium text-emerald-100">
                          / {targetEnergy} Kcal
                        </span>
                      </div>
                    </div>
                    {(oralItems.length > 0 || parenteralItems.length > 0) && (
                      <div className={`backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-2 border text-xs font-semibold ${smartRecallBadge.badgeBg}`}>
                        <span className={`w-2 h-2 rounded-full ${smartRecallBadge.dotColor} animate-pulse`}></span>
                        <span>{percentageOfTarget}% Target • {smartRecallBadge.label}</span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-3 w-full bg-black/20 rounded-full h-2.5 overflow-hidden p-0.5">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        numericPct > 130
                          ? 'bg-rose-400'
                          : numericPct >= 80 && numericPct <= 120 
                            ? 'bg-emerald-300' 
                            : numericPct >= 50 && numericPct < 80 
                              ? 'bg-amber-300' 
                              : numericPct > 120 && numericPct <= 130
                                ? 'bg-orange-300'
                                : 'bg-red-400'
                      }`}
                      style={{ width: `${Math.min(numericPct, 100)}%` }}
                    />
                  </div>

                  {/* Alert Peringatan Risiko Overfeeding & Defisit */}
                  {numericPct > 130 && (oralItems.length > 0 || parenteralItems.length > 0) && (
                    <div className="mt-3 p-2.5 rounded-xl bg-rose-500/30 border border-rose-200/50 backdrop-blur-md flex items-start gap-2.5 text-xs text-white">
                      <AlertTriangle className="h-4 w-4 text-rose-200 flex-shrink-0 mt-0.5 animate-bounce" />
                      <div>
                        <p className="font-bold text-rose-100">Waspada Overfeeding ({percentageOfTarget}% Target)</p>
                        <p className="text-[11px] text-rose-100/90 mt-0.5 leading-relaxed">
                          Asupan telah melampaui 130% target kebutuhan. Pantau ketat risiko hiperglikemia, hiperkapnia, azotemia, atau refeeding syndrome.
                        </p>
                      </div>
                    </div>
                  )}
                  {numericPct < 50 && (oralItems.length > 0 || parenteralItems.length > 0) && (
                    <div className="mt-3 p-2.5 rounded-xl bg-red-500/25 border border-red-200/40 backdrop-blur-md flex items-start gap-2 text-xs text-white">
                      <AlertCircle className="h-4 w-4 text-red-200 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold text-red-100">Defisit Berat ({percentageOfTarget}% Target): </span>
                        <span className="text-[11px] text-red-100/90">Asupan di bawah 50% target. Pertimbangkan eskalasi diet enteral atau rute parenteral.</span>
                      </div>
                    </div>
                  )}
                </div>

                <CardContent className="p-4 sm:p-5 space-y-4">
                  {/* Macronutrient Metric Pills */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2.5 rounded-xl bg-emerald-50/70 border border-emerald-100 text-center">
                      <p className="text-[11px] font-semibold text-emerald-700">Protein</p>
                      <p className="text-base font-bold text-slate-900 mt-0.5">{formatNumber(grandTotal.protein)}g</p>
                      <p className="text-[10px] text-emerald-600 font-medium">{grandTotalPercentages.proteinPercent}% kkal</p>
                    </div>

                    <div className="p-2.5 rounded-xl bg-purple-50/70 border border-purple-100 text-center">
                      <p className="text-[11px] font-semibold text-purple-700">Karbohidrat</p>
                      <p className="text-base font-bold text-slate-900 mt-0.5">{formatNumber(grandTotal.carbs)}g</p>
                      <p className="text-[10px] text-purple-600 font-medium">{grandTotalPercentages.carbPercent}% kkal</p>
                    </div>

                    <div className="p-2.5 rounded-xl bg-amber-50/70 border border-amber-100 text-center">
                      <p className="text-[11px] font-semibold text-amber-700">Lemak</p>
                      <p className="text-base font-bold text-slate-900 mt-0.5">{formatNumber(grandTotal.fat)}g</p>
                      <p className="text-[10px] text-amber-600 font-medium">{grandTotalPercentages.fatPercent}% kkal</p>
                    </div>
                  </div>

                  {/* Donut Chart Macronutrient Distribution */}
                  {(oralItems.length > 0 || parenteralItems.length > 0) && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs font-semibold text-slate-600 mb-1 text-center">Distribusi Rasio Kalori Makronutrien</p>
                      <div className="h-44 relative flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={46}
                              outerRadius={68}
                              paddingAngle={4}
                              dataKey="value"
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              formatter={(value, name) => [`${formatNumber(value)}%`, name]} 
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-2">
                          <span className="text-[11px] text-slate-400 font-medium">Status Asupan</span>
                          <span className={`text-xs font-bold ${smartRecallBadge.chartColor}`}>
                            {smartRecallBadge.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Komparasi Rute Asupan (Oral vs Enteral vs Parenteral) */}
                  {(oralItems.length > 0 || parenteralItems.length > 0) && (
                    <div className="pt-3 border-t border-slate-100 space-y-2.5">
                      <div className="flex items-center justify-between gap-1 flex-wrap">
                        <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                          <Layers className="h-3.5 w-3.5 text-sky-600" />
                          Komparasi Rute Asupan
                        </span>
                        <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold border ${routeBadge.badgeClass}`}>
                          {routeBadge.label}
                        </span>
                      </div>

                      {/* 3-Color Segmented Progress Bar */}
                      <div className="h-2.5 w-full bg-slate-200 rounded-full overflow-hidden flex">
                        {routeSplits.oralPct > 0 && (
                          <div
                            style={{ width: `${routeSplits.oralPct}%` }}
                            className="bg-emerald-500 h-full transition-all"
                            title={`Oral: ${routeSplits.oralPct}%`}
                          />
                        )}
                        {routeSplits.enteralPct > 0 && (
                          <div
                            style={{ width: `${routeSplits.enteralPct}%` }}
                            className="bg-sky-500 h-full transition-all"
                            title={`Enteral: ${routeSplits.enteralPct}%`}
                          />
                        )}
                        {routeSplits.parenteralPct > 0 && (
                          <div
                            style={{ width: `${routeSplits.parenteralPct}%` }}
                            className="bg-purple-500 h-full transition-all"
                            title={`Parenteral: ${routeSplits.parenteralPct}%`}
                          />
                        )}
                      </div>

                      {/* Route Details Cards */}
                      <div className="grid grid-cols-3 gap-1.5 text-center text-[10px]">
                        <div className="p-1.5 rounded-lg bg-emerald-50/80 border border-emerald-100">
                          <p className="font-bold text-emerald-800">🥗 Oral</p>
                          <p className="font-bold text-slate-900 text-[11px] mt-0.5">{routeSplits.oralKcal} <span className="text-[9px] font-normal">Kcal</span></p>
                          <p className="text-emerald-700 text-[9.5px] font-semibold">{routeSplits.oralPct}%</p>
                        </div>
                        <div className="p-1.5 rounded-lg bg-sky-50/80 border border-sky-100">
                          <p className="font-bold text-sky-800">🥣 Enteral</p>
                          <p className="font-bold text-slate-900 text-[11px] mt-0.5">{routeSplits.enteralKcal} <span className="text-[9px] font-normal">Kcal</span></p>
                          <p className="text-sky-700 text-[9.5px] font-semibold">{routeSplits.enteralPct}%</p>
                        </div>
                        <div className="p-1.5 rounded-lg bg-purple-50/80 border border-purple-100">
                          <p className="font-bold text-purple-800">💉 Parenteral</p>
                          <p className="font-bold text-slate-900 text-[11px] mt-0.5">{routeSplits.parenteralKcal} <span className="text-[9px] font-normal">Kcal</span></p>
                          <p className="text-purple-700 text-[9.5px] font-semibold">{routeSplits.parenteralPct}%</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons: Always Accessible */}
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <Button 
                      size="lg" 
                      onClick={handleSave} 
                      disabled={saving}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm h-11 shadow-sm gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Menyimpan Perhitungan...' : 'Simpan Perhitungan Recall'}
                    </Button>

                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="outline" 
                        onClick={handleExportPDF} 
                        disabled={exporting}
                        className="text-xs h-9 border-emerald-300 text-emerald-800 bg-emerald-50/50 hover:bg-emerald-100 hover:text-emerald-900 gap-1.5 font-semibold"
                        title="Cetak Resume Medis Asuhan Gizi Klinis format PDF"
                      >
                        <FileDown className="h-3.5 w-3.5 text-emerald-600" />
                        {exporting ? 'Membuat PDF...' : 'Cetak Resume Medis'}
                      </Button>

                      <Button 
                        variant="ghost" 
                        onClick={handleClearAll}
                        className="text-xs h-9 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Kosongkan
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        </main>
      </div>

      {/* Dialogs */}
      <AddFoodDialog
        open={showFoodDialog}
        onClose={() => setShowFoodDialog(false)}
        onAdd={handleAddOralItem}
        defaultRoute={activeInputTab === 'enteral' ? 'enteral' : 'oral'}
      />
      <AddParenteralDialog
        open={showParenteralDialog}
        onClose={() => setShowParenteralDialog(false)}
        onAdd={handleAddParenteralItem}
      />

      {/* Edit Quantity & Route Dialog */}
      {editingItem && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent
            className="max-w-sm rounded-xl"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (e.target.tagName === 'BUTTON') return;
                e.preventDefault();
                handleSaveEdit();
              }
            }}
          >
            <DialogHeader>
              <DialogTitle className="text-base">
                {editingItem.type === 'oral' ? 'Ubah Porsi & Rute Asupan' : 'Ubah Jumlah / Dosis'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Perbarui porsi untuk: <strong>{editingItem.name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              {editingItem.type === 'oral' && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700">Rute Pemberian Asupan</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setEditRoute('oral')}
                      className={`flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-lg text-xs font-semibold border transition-all ${
                        editRoute === 'oral'
                          ? 'bg-emerald-50 text-emerald-900 border-emerald-500 ring-2 ring-emerald-500/20 shadow-xs'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <span>🥗</span> Oral
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditRoute('enteral')}
                      className={`flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-lg text-xs font-semibold border transition-all ${
                        editRoute === 'enteral'
                          ? 'bg-sky-50 text-sky-900 border-sky-500 ring-2 ring-sky-500/20 shadow-xs'
                          : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <span>🥣</span> Enteral
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-quantity" className="text-xs font-medium">
                  Jumlah ({editingItem.unit})
                </Label>
                <Input
                  id="edit-quantity"
                  type="number"
                  step="0.1"
                  min="0.1"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveEdit();
                    }
                  }}
                  placeholder="Masukkan jumlah baru"
                  autoFocus
                  className="h-10 text-sm"
                />

                {/* Quick Quantity Chips */}
                <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                  <span className="text-[10.5px] font-medium text-slate-400 mr-0.5">Pilihan Cepat:</span>
                  {(editingItem.type === 'oral' 
                    ? [0.25, 0.5, 0.75, 1, 1.5, 2, 3] 
                    : [0.5, 1, 1.5, 2, 3, 4]
                  ).map((qty) => {
                    const isActive = parseFloat(editQuantity) === qty;
                    const isOral = editingItem.type === 'oral';
                    const activeBg = isOral && editRoute === 'enteral'
                      ? 'bg-sky-600 text-white border-sky-600 shadow-xs'
                      : isOral
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-sky-600 text-white border-sky-600 shadow-xs';
                    const label = isOral
                      ? (qty === 0.25 ? '¼' : qty === 0.5 ? '½' : qty === 0.75 ? '¾' : qty) + ' porsi'
                      : (qty === 0.5 ? '½ unit' : `${qty} unit`);

                    return (
                      <button
                        key={qty}
                        type="button"
                        onClick={() => setEditQuantity(qty)}
                        className={`px-2 py-0.5 rounded-lg text-xs font-semibold transition-all border ${
                          isActive
                            ? activeBg
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <p className="text-[11px] text-slate-400">
                  Saat ini: {editingItem.quantity} × {editingItem.unit}
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowEditDialog(false)} className="text-xs">
                Batal
              </Button>
              <Button size="sm" onClick={handleSaveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-xs">
                Simpan Perubahan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm & Alert Dialogs */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />

      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
        buttonText={alertDialog.buttonText}
      />
      
      {/* Sticky Mobile Bottom Summary Bar */}
      <div 
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/90 px-3.5 pt-2.5 shadow-[0_-4px_25px_rgba(0,0,0,0.1)] flex items-center justify-between gap-2.5"
        style={{ paddingBottom: 'max(14px, env(safe-area-inset-bottom, 14px))' }}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Asupan:</span>
            <span className="text-sm font-bold text-slate-900">{Math.round(grandTotal.energy)}</span>
            <span className="text-[11px] text-slate-400">/{targetEnergy} Kcal</span>
            <span className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold border ${smartRecallBadge.pillBg} truncate max-w-[140px]`}>
              {percentageOfTarget}% • {smartRecallBadge.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mt-0.5 font-medium">
            <span>P: <strong className="text-slate-800">{formatNumber(grandTotal.protein)}g</strong></span>
            <span className="text-slate-300">•</span>
            <span>KH: <strong className="text-slate-800">{formatNumber(grandTotal.carbs)}g</strong></span>
            <span className="text-slate-300">•</span>
            <span>L: <strong className="text-slate-800">{formatNumber(grandTotal.fat)}g</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-[9.5px] mt-0.5 font-semibold">
            <span className="text-emerald-700">🥗 {routeSplits.oralKcal}</span>
            <span className="text-slate-300">•</span>
            <span className="text-sky-700">🥣 {routeSplits.enteralKcal}</span>
            <span className="text-slate-300">•</span>
            <span className="text-purple-700">💉 {routeSplits.parenteralKcal} Kcal</span>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving || (oralItems.length === 0 && parenteralItems.length === 0)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs px-3.5 py-2 h-9 rounded-xl shadow-md shadow-emerald-600/20 flex items-center gap-1.5 flex-shrink-0"
        >
          {saving ? (
            <>
              <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Menyimpan...</span>
            </>
          ) : (
            <>
              <Save className="h-3.5 w-3.5" />
              <span>Simpan</span>
            </>
          )}
        </Button>
      </div>

      <BackToTop className="bottom-24 lg:bottom-6 right-4 lg:right-6" />
    </div>
  );
}
